import { useEffect, useRef } from "react";

import useBreadcrumbs from "components/Breadcrumbs/useBreadcrumbs";
import DocumentationButton from "components/DocumentationButton";
import NotFoundPage from "components/error/NotFoundPage";
import { EmptystatePrColored } from "components/icons/generated";
import PageLoading from "components/loading/PageLoading";
import PageWrapper from "components/PageWrapper";
import { HUMANIZE_PROVIDER } from "constants/vcsProviders";
import Button from "ds/components/Button";
import EmptyState from "ds/components/EmptyState";
import PageInfoHeader from "ds/components/PageInfoHeader";
import Table from "ds/components/Table";
import TableBody from "ds/components/Table/Body";
import TableWrapper from "ds/components/Table/Wrapper";
import TableSimpleHead from "ds/components/TableSimple/Head";
import Typography from "ds/components/Typography";
import useErrorHandle from "hooks/useErrorHandle";
import useTitle from "hooks/useTitle";
import useTypedContext from "hooks/useTypedContext";
import { getDocsUrl } from "utils/getDocsUrl";

import { ModuleContext } from "../Context";
import ModulePullRequestsTableRows from "./Table/Rows";
import useModulePullRequests from "./useModulePullRequests";
import useModulePullRequestsTableExpandedRowsStore from "./Table/useExpandedRowsStore";
import useModulePullRequestsTableStore from "./Table/useTableStore";

const TABLE_ARIA_LABEL = "Pull requests table";

const ModulePullRequests = () => {
  const { module } = useTypedContext(ModuleContext);
  const tableRef = useRef<HTMLDivElement>(null);

  const resetExpandedRows = useModulePullRequestsTableExpandedRowsStore((state) => state.reset);

  useEffect(() => {
    resetExpandedRows();
  }, [module.id, resetExpandedRows]);

  useTitle(`Pull Requests · ${module.id}`);

  useBreadcrumbs([
    {
      title: "Modules",
      link: "/modules",
    },
    {
      title: module.id,
    },
  ]);

  const { pullRequests, error, isPageLoading, isRefreshing, isEmpty, isNotFound, refresh } =
    useModulePullRequests(module.id);

  const ErrorContent = useErrorHandle(error);

  if (ErrorContent) {
    return ErrorContent;
  }

  if (isPageLoading) {
    return <PageLoading />;
  }

  if (isNotFound) {
    return <NotFoundPage />;
  }

  return (
    <>
      <PageInfoHeader title="Pull requests">
        <Button onPress={refresh} loading={isRefreshing} variant="primary">
          Refresh
        </Button>
      </PageInfoHeader>

      <PageWrapper margin="0">
        <TableWrapper ref={tableRef} hasPagination={false}>
          {isEmpty && (
            <EmptyState
              title="No pull requests yet"
              icon={EmptystatePrColored}
              caption={
                <>
                  This is a list of Pull Requests currently open against this module's tracked
                  branch on {HUMANIZE_PROVIDER[module.provider]}.<br />
                  If something seems missing, it may be because of two reasons:
                  <br />
                  1) The Pull Request was opened before the creation of this module, or it may be
                  targeting a different branch.
                  <br />
                  2) You do not have a{" "}
                  <Typography tag="span" variant="s-body3">
                    .spacelift/config.yml
                  </Typography>{" "}
                  file that contains at least one test case.
                </>
              }
            >
              <DocumentationButton
                to={getDocsUrl("/vendors/terraform/module-registry#git-repository-structure")}
              />
            </EmptyState>
          )}

          {!isEmpty && (
            <Table ariaLabel={TABLE_ARIA_LABEL} role="treegrid">
              <TableSimpleHead
                store={useModulePullRequestsTableStore}
                ariaLabel={TABLE_ARIA_LABEL}
                tableRef={tableRef}
              />
              <TableBody>
                <ModulePullRequestsTableRows pullRequests={pullRequests} tableRef={tableRef} />
              </TableBody>
            </Table>
          )}
        </TableWrapper>
      </PageWrapper>
    </>
  );
};

export default ModulePullRequests;
